// Configuração unificada de administradores.
// - Super Admins: definidos em VITE_SUPER_ADMIN_EMAILS (env), sempre admins.
// - Admins regulares: gerenciados via Firebase (collection 'admins').
// - Admins têm acesso a: testnets, painel de diagnóstico, gerenciamento de admins.

/**
 * Normaliza e-mail para lowercase.
 */
export function normalizeEmail(email) {
  if (!email || typeof email !== 'string') return '';
  return email.trim().toLowerCase();
}

/**
 * Remove BOM (Byte Order Mark) e caracteres invisíveis no início/fim.
 * Arquivos .env salvos como "UTF-8 with BOM" no Windows podem causar variável undefined.
 */
export function stripBomAndTrim(str) {
  if (str == null || typeof str !== 'string') return '';
  return str.replace(/^\uFEFF/, '').trim();
}

/**
 * Lista de Super Admins (do .env.local via VITE_SUPER_ADMIN_EMAILS).
 * Super Admins sempre têm privilégios, mesmo se não estiverem no Firebase.
 */
const RAW_SUPER_ADMIN_EMAILS = typeof import.meta !== 'undefined'
  ? import.meta.env?.VITE_SUPER_ADMIN_EMAILS ?? ''
  : '';

export const SUPER_ADMIN_EMAILS = stripBomAndTrim(RAW_SUPER_ADMIN_EMAILS)
  ? stripBomAndTrim(RAW_SUPER_ADMIN_EMAILS)
      .split(',')
      .map((e) => normalizeEmail(e))
      .filter(Boolean)
  : [];

/**
 * Exposto apenas para debug: retorna o valor cru de VITE_SUPER_ADMIN_EMAILS.
 */
export function getRawSuperAdminEnv() {
  try {
    return RAW_SUPER_ADMIN_EMAILS || null;
  } catch {
    return null;
  }
}

/**
 * Verifica se um e-mail é Super Admin.
 */
export function isSuperAdmin(email) {
  if (!email) return false;
  return SUPER_ADMIN_EMAILS.includes(normalizeEmail(email));
}

/**
 * Verifica se um e-mail é Admin (Super Admin ou na lista do Firebase).
 * @param {string} email - E-mail a verificar
 * @param {Array<string|{email: string}>} adminList - Lista de admins do Firebase (pode ser array de strings ou objetos)
 * @returns {boolean}
 */
export function isAdmin(email, adminList = []) {
  if (!email) return false;
  
  const normalized = normalizeEmail(email);
  
  // Super Admin sempre é admin
  if (isSuperAdmin(normalized)) return true;
  
  // Verificar na lista do Firebase
  if (!Array.isArray(adminList)) return false;
  
  return adminList.some((admin) => {
    const adminEmail = typeof admin === 'string' ? admin : admin?.email;
    return normalizeEmail(adminEmail) === normalized;
  });
}
